/** @format */

import React from 'react'
import { FormPath } from '@formily/core'
import { toJS } from '@formily/reactive'
import {
  ArrayField,
  Field as InternalField,
  ObjectField,
  VoidField,
  observer,
  ISchema,
  Schema
} from '@formily/react'
import { FormItem } from '@formily/antd'
import { createBehavior } from '@designable/core'
import { useDesigner, useTreeNode, useComponents, DnFC } from '@designable/react'
import { Container } from '../../common/Container'
import { AllLocales } from '../../locales'

Schema.silent(true)

const SchemaStateMap = {
  title: 'title',
  description: 'description',
  default: 'value',
  enum: 'dataSource',
  readOnly: 'readOnly',
  writeOnly: 'editable',
  required: 'required',
  'x-content': 'content',
  'x-value': 'value',
  'x-editable': 'editable',
  'x-disabled': 'disabled',
  'x-read-pretty': 'readPretty',
  'x-read-only': 'readOnly',
  'x-visible': 'visible',
  'x-hidden': 'hidden',
  'x-display': 'display',
  'x-pattern': 'pattern'
}

const NeedShownExpression = {
  title: true,
  description: true,
  default: true,
  'x-content': true,
  'x-value': true
}

const isExpression = (val: unknown): val is string =>
  typeof val === 'string' && /^\{\{.*\}\}$/.test(val)

const filterExpression = (val: unknown): unknown => {
  if (val !== null && typeof val === 'object') {
    if (Array.isArray(val)) {
      const arr: unknown[] = []
      for (const item of val) {
        if (isExpression(item)) continue
        const nested = filterExpression(item)
        if (nested !== undefined && nested !== null) arr.push(nested)
      }
      return arr
    }
    const obj: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      if (isExpression(v)) continue
      const nested = filterExpression(v)
      if (nested !== undefined && nested !== null) obj[k] = nested
    }
    return obj
  }
  if (isExpression(val)) return undefined
  return val
}

const toDesignableFieldProps = (
  schema: ISchema,
  components: Record<string, unknown>,
  nodeIdAttrName: string,
  id: string
) => {
  const results: Record<string, unknown> = {}
  for (const [schemaKey, fieldKey] of Object.entries(SchemaStateMap)) {
    const value = (schema as unknown as Record<string, unknown>)[schemaKey]
    if (isExpression(value)) {
      if (!NeedShownExpression[schemaKey]) continue
      if (value) {
        results[fieldKey] = value
        continue
      }
    } else if (value !== undefined && value !== null) {
      results[fieldKey] = filterExpression(value)
    }
  }
  if (!('FormItem' in components)) {
    ;(components as Record<string, unknown>)['FormItem'] = FormItem
  }
  const decoratorPath = schema['x-decorator'] as string | undefined
  const componentPath = schema['x-component'] as string | undefined
  const decorator = decoratorPath && (FormPath.getIn(components, decoratorPath) as unknown)
  const component = componentPath && (FormPath.getIn(components, componentPath) as unknown)
  const decoratorProps = (schema['x-decorator-props'] ?? {}) as Record<string, unknown>
  const componentProps = (schema['x-component-props'] ?? {}) as Record<string, unknown>

  if (decorator) {
    results.decorator = [decorator, toJS(decoratorProps)]
  }
  if (component) {
    results.component = [component, toJS(componentProps)]
  }
  if (decorator) {
    FormPath.setIn(
      (results['decorator'] as unknown as [unknown, Record<string, unknown>])[1],
      nodeIdAttrName,
      id
    )
  } else if (component) {
    FormPath.setIn(
      (results['component'] as unknown as [unknown, Record<string, unknown>])[1],
      nodeIdAttrName,
      id
    )
  }
  if (results.title) {
    results.title = <span data-content-editable="title">{results.title as React.ReactNode}</span>
  }
  if (results.description) {
    results.description = (
      <span data-content-editable="description">{results.description as React.ReactNode}</span>
    )
  }
  return results
}

export const Field: DnFC<ISchema> = observer(props => {
  const designer = useDesigner()
  const components = useComponents()
  const node = useTreeNode()
  if (!node) return null
  const fieldProps = toDesignableFieldProps(
    props,
    components,
    designer.props.nodeIdAttrName,
    node.id
  )
  if (props.type === 'object') {
    return (
      <Container>
        <ObjectField {...fieldProps} name={node.id}>
          {props.children}
        </ObjectField>
      </Container>
    )
  } else if (props.type === 'array') {
    return <ArrayField {...fieldProps} name={node.id} />
  } else if (node.props.type === 'void') {
    return (
      <VoidField {...fieldProps} name={node.id}>
        {props.children}
      </VoidField>
    )
  }
  return <InternalField {...fieldProps} name={node.id} />
})

Field.Behavior = createBehavior({
  name: 'Field',
  selector: 'Field',
  designerLocales: AllLocales.Field
})
